import Stack from "@mui/material/Stack";
import { theme } from "@polifonia/theme";
import { useTranslation } from "react-i18next";

import { RouterLink } from "@/components/RouterLink";
import { Tag } from "@/components/Tag";
import { Typography } from "@/components/Typography";
import { useEnableBillingV2 } from "@/features/billing/useEnableBillingV2";
import { useEnableUcDashboard } from "@/features/billing/useEnableUcDashboard";
import { useOrgChannels } from "@/features/channels/useOrgChannels";
import { useEnableCustomFieldCard } from "@/features/customField/useEnableCustomFieldCard";
import { UnifyKeyLabel } from "@/features/unify-scope-setting/UnifyKeyLabel";
import { interlude } from "@/interlude";
import { ActiveContactsCard } from "@/pages/components/ActiveContactsCard";
import { SummaryCard, SummaryCardGrid } from "@/pages/components/SummaryCard";
import { DataFlow } from "@/pages/dataHub/DataFlow";

const MergeRulesCard: React.FC = function MergeRulesCard() {
  const { t } = useTranslation();
  const unifyScopeSettingQuery =
    interlude.unifyScopeSetting.useGetUnifyScopeSetting();
  const mergeStateQuery = interlude.unifyScopeSetting.useGetUnifyMergeState();

  if (!unifyScopeSettingQuery.isSuccess) return null;

  return (
    <SummaryCard
      title={t("dataHub.rules.title", "Merge rules")}
      action={
        <RouterLink to="/data-hub" search={{ tab: "rules" }}>
          {t("dataHub.rules.edit", "Edit merge rules")}
        </RouterLink>
      }
    >
      <Typography variant="note" color={theme.colors.staticFgNote}>
        {t(
          "dataHub.rules.summary",
          "Profiles that share any of these identifiers are merged into one customer.",
        )}
      </Typography>
      <Stack direction="row" gap="8px" flexWrap="wrap">
        {unifyScopeSettingQuery.data.unifyKeys.map((key) => (
          <Tag
            key={key.attribute}
            label={<UnifyKeyLabel unifyKey={key.attribute} />}
          />
        ))}
      </Stack>
      {mergeStateQuery.isSuccess ? (
        <Typography variant="body">
          {mergeStateQuery.data.isFinished
            ? t("applications.cdh.merged.title")
            : t("cdh.unification.merging.label")}
        </Typography>
      ) : null}
    </SummaryCard>
  );
};

const DataSourcesCard: React.FC = function DataSourcesCard() {
  const { t } = useTranslation();
  const orgChannels = useOrgChannels();
  const hasEcModuleQuery = interlude.organization.useHasEcModule();
  const ecPlatformCount = hasEcModuleQuery.data?.ecPlatforms?.length;

  if (!orgChannels.isSuccess) return null;

  return (
    <SummaryCard
      title={t("dataHub.sources.title", "Data sources")}
      action={
        <RouterLink to="/data-hub" search={{ tab: "sources" }}>
          {t("dataHub.sources.view", "View data sources")}
        </RouterLink>
      }
    >
      <Typography variant="body">
        {t("dataHub.sources.channelCount", {
          count: orgChannels.channels.length,
          defaultValue: "{{count}} channels",
        })}
      </Typography>
      {ecPlatformCount === undefined ? null : (
        <Typography variant="body">
          {t("dataHub.sources.ecommerceCount", {
            count: ecPlatformCount,
            defaultValue: "{{count}} e-commerce platforms",
          })}
        </Typography>
      )}
    </SummaryCard>
  );
};

const CustomFieldsCard: React.FC = function CustomFieldsCard() {
  const { t } = useTranslation();
  const customFieldListQuery = interlude.customField.useListCustomFields({
    queries: { enable_single_select: true },
  });
  const organizationSettingQuery =
    interlude.organization.useOrganizationSetting();

  if (!customFieldListQuery.isSuccess || !organizationSettingQuery.isSuccess)
    return null;
  // Orgs without a custom field allowance have nothing to show here; the
  // Applications card offers them a contact link instead.
  if (organizationSettingQuery.data.customFieldLimit <= 0) return null;

  return (
    <SummaryCard
      title={t("applications.customField.title")}
      action={
        <RouterLink to="/applications/custom-fields">
          {t("common.manage", "Manage")}
        </RouterLink>
      }
    >
      <Typography variant="value">
        {t("dataHub.customFields.usage", {
          used: customFieldListQuery.data.length,
          limit: organizationSettingQuery.data.customFieldLimit,
          defaultValue: "{{used}} / {{limit}}",
        })}
      </Typography>
      <Typography variant="note" color={theme.colors.staticFgNote}>
        {t(
          "dataHub.customFields.note",
          "Fields in use out of your plan's limit",
        )}
      </Typography>
    </SummaryCard>
  );
};

/**
 * What profile unification is doing for a connected org: its merge rules, where its data
 * comes from and how many custom fields it uses. Each card appears only once
 * its data has loaded.
 */
export const DataHubStatusTab: React.FC = function DataHubStatusTab() {
  const enableCustomFieldCard = useEnableCustomFieldCard();
  const billingV2 = useEnableBillingV2();
  const enableUcDashboard = useEnableUcDashboard();
  return (
    <Stack gap="16px">
      <DataFlow />
      <SummaryCardGrid>
        {/* Same gate as the billing overview chart it comes from. */}
        {billingV2.isEnabled && enableUcDashboard ? (
          <ActiveContactsCard />
        ) : null}
        <MergeRulesCard />
        <DataSourcesCard />
        {enableCustomFieldCard ? <CustomFieldsCard /> : null}
      </SummaryCardGrid>
    </Stack>
  );
};
